import { supabase } from './supabase';
import { logger, LogCategory } from './logger';

/**
 * Interface for email template data
 *
 * Database schema:
 * - id: uuid not null default uuid_generate_v4()
 * - category: text not null (enum value)
 * - template_name: text not null
 * - subject: text not null
 * - content: text not null
 * - created_at: timestamp with time zone null default now()
 * - updated_at: timestamp with time zone null default now()
 * - created_by: uuid null
 * - is_system: boolean null default false
 */
export interface EmailTemplate {
  id: string;
  category: string;
  template_name: string;
  subject: string;
  content: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  is_system?: boolean;
}

/**
 * Interface for template variable values
 */
export interface TemplateVariables {
  [key: string]: string | number | boolean | null;
}

/**
 * Email Template Service
 * Provides functionality for working with email templates
 */
export class EmailTemplateService {
  /**
   * Fetch an email template by ID
   * @param templateId - The ID of the template to fetch
   * @returns The email template or null if not found
   */
  async getTemplateById(templateId: string): Promise<EmailTemplate | null> {
    try {
      logger.info(`Fetching email template with ID: ${templateId}`, LogCategory.DATABASE);
      
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('id', templateId)
        .single();
      
      if (error) {
        logger.error(`Error fetching email template: ${error.message}`, LogCategory.DATABASE);
        return null;
      }
      
      if (!data) {
        logger.warn(`Email template with ID ${templateId} not found`, LogCategory.DATABASE);
        return null;
      }
      
      return data as EmailTemplate;
    } catch (error: any) {
      logger.error(`Unexpected error fetching email template: ${error.message}`, LogCategory.DATABASE);
      return null;
    }
  }
  
  /**
   * Fetch an email template by process step type
   * @param processStepType - The process step type to fetch the template for
   * @returns The email template or null if not found
   */
  async getTemplateByProcessStepType(processStepType: string): Promise<EmailTemplate | null> {
    try {
      logger.info(`Fetching email template for process step: ${processStepType}`, LogCategory.DATABASE);
      
      // First get the mapping
      const { data: mapping, error: mappingError } = await supabase
        .from('email_template_process_mappings')
        .select('email_template_id')
        .eq('process_step_type', processStepType)
        .eq('is_active', true)
        .single();
      
      if (mappingError) {
        logger.error(`Error fetching template mapping: ${mappingError.message}`, LogCategory.DATABASE);
        return null;
      }
      
      if (!mapping) {
        logger.warn(`No template mapping found for process step ${processStepType}`, LogCategory.DATABASE);
        return null;
      }
      
      // Then get the template
      return await this.getTemplateById(mapping.email_template_id);
    } catch (error: any) {
      logger.error(`Unexpected error fetching template by process step: ${error.message}`, LogCategory.DATABASE);
      return null;
    }
  }
  
  /**
   * Process an email template by replacing variables with actual values
   * @param template - The email template to process
   * @param variables - Object containing variable values to replace in the template
   * @returns The processed template with variables replaced
   */
  processTemplate(template: EmailTemplate, variables: TemplateVariables): EmailTemplate {
    try {
      logger.info(`Processing email template: ${template.template_name}`, LogCategory.GENERAL);
      
      // Create a copy of the template to avoid modifying the original
      const processedTemplate: EmailTemplate = { ...template };
      
      // Process subject
      processedTemplate.subject = this.replaceVariables(template.subject, variables);
      
      // Process body (content)
      processedTemplate.content = this.replaceVariables(template.content, variables);
      
      return processedTemplate;
    } catch (error: any) {
      logger.error(`Error processing email template: ${error.message}`, LogCategory.GENERAL);
      // Return the original template if there's an error
      return template;
    }
  }
  
  /**
   * Replace variables in a string with their values
   * @param text - The text containing variables to replace
   * @param variables - Object containing variable values
   * @returns The text with variables replaced
   */
  private replaceVariables(text: string, variables: TemplateVariables): string {
    if (!text) return '';
    
    // Replace all variables in the format {{variableName}}
    return text.replace(/\{\{([^}]+)\}\}/g, (match, variableName) => {
      const trimmedName = variableName.trim();
      
      // Check if the variable exists in the provided variables object
      if (Object.prototype.hasOwnProperty.call(variables, trimmedName)) {
        const value = variables[trimmedName];
        // Convert to string, or use empty string for null/undefined
        return value !== null && value !== undefined ? String(value) : '';
      }
      
      // If variable not found, log a warning and return the original variable placeholder
      logger.warn(`Variable not found: ${trimmedName}`, LogCategory.GENERAL);
      return match;
    });
  }
}

// Export a singleton instance
export const emailTemplateService = new EmailTemplateService();